import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, HOSPITAL_DEMO, INK } from "../../theme/tokens";
import type { AreaSplit } from "./recommendationBreakdown";

type Props = {
	parts: AreaSplit[];
	/** Legend mode: the area's NAME in muted uppercase, not its value. Used under the date. */
	asLegend?: boolean;
};

/**
 * The per-area line under a Shift-Insights sidebar metric, e.g. "● 5  ● 13" beneath a census of 18.
 *
 * Dot colors come from the same ordered ramp as the graph hover's breakdown, so an area reads as the
 * same color in both places.
 */
/** Dot diameter for an area swatch. See the comment at its use site for why not BADGE.DOT_SIZE. */
const AREA_DOT_SIZE = 6;

/** Unit names are department configuration, shown as-is — only the abbreviation is our copy. */
const TELEMETRY_UNIT_PREFIX = "tele";

export const AreaSplitRow: FC<Props> = ({ parts, asLegend }) => {
	const { t } = useTranslation();
	// The rail is narrow enough that "Telemetry" wraps, so the legend shows a translated short form.
	// Every other unit name is the department's own configuration and is rendered verbatim.
	const legendLabel = (unit: string): string => (unit.trim().toLowerCase().startsWith(TELEMETRY_UNIT_PREFIX) ? t("shift.insights.area.telemetry.short") : unit);

	if (parts.length < 2) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
			{parts.map((part, index) => (
				<Box key={part.unit} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<Box
						sx={{
							// Deliberately smaller than BADGE.DOT_SIZE (8px): these dots sit inline with 12-13px
							// text in a narrow rail, where the badge size crowds the number beside it.
							width: AREA_DOT_SIZE,
							height: AREA_DOT_SIZE,
							borderRadius: "50%",
							flexShrink: 0,
							backgroundColor: HOSPITAL_DEMO.BREAKDOWN_AREA_COLORS[index % HOSPITAL_DEMO.BREAKDOWN_AREA_COLORS.length].dot,
						}}
					/>
					<Typography
						sx={{
							fontSize: asLegend ? FONT_SIZE.CAPTION : FONT_SIZE.SECONDARY,
							fontWeight: 600,
							...(asLegend && { textTransform: "uppercase", letterSpacing: "0.06em" }),
							// Legend labels are muted (the dot carries the identity); values keep the area color.
							color: asLegend ? INK.MUTED : HOSPITAL_DEMO.BREAKDOWN_AREA_COLORS[index % HOSPITAL_DEMO.BREAKDOWN_AREA_COLORS.length].text,
						}}
					>
						{asLegend ? legendLabel(part.unit) : part.value}
					</Typography>
				</Box>
			))}
		</Box>
	);
};
